import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Copy } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import Tooltip from 'components/reusable/Tooltip'
import { adminRecordInspector } from 'rails_routes'

import LedgerPathDisplay from './LedgerPathDisplay'
import { useGetLedgerEntries } from '../../hooks/useGetLedgerEntries'
import { formatMoney } from '../../utils'
import TanstackTable from '../reusable/table/TanstackTable'

interface LedgerEntry {
  id: string
  amount: { value: string; currency: string }
  description: string | null
  idempotency_key: string
  metadata: Record<string, unknown> | null
  source_type: string
  source_id: string
  ledger_account_id: string
  ledger_account_path: string
  created_at: string
  updated_at: string
}

const columnHelper = createColumnHelper<LedgerEntry>()

const columns = [
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: (info) => formatMoney(parseFloat(info.getValue().value)),
  }),
  columnHelper.accessor('description', {
    header: 'Description',
  }),
  columnHelper.accessor('ledger_account_path', {
    header: 'Account Path',
    cell: (info) => (
      <div className='flex items-center justify-between gap-2'>
        <LedgerPathDisplay path={info.getValue()} />
        <CopyButtonWithAnimation content={info.getValue()}>
          <Copy size={16} strokeWidth={1} />
        </CopyButtonWithAnimation>
      </div>
    ),
  }),
  columnHelper.accessor('updated_at', {
    header: 'Updated Date',
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
  }),
  columnHelper.accessor('id', {
    header: 'ID',
    cell: (info) => (
      <div className='flex items-center justify-between'>
        <Tooltip triggerChildren={
          <a href={adminRecordInspector.show({ id: info.getValue() })}>
            {info.getValue().substring(info.getValue().length - 5)}
          </a>
        }
        >
          <div className='text-xs p-2 rounded-md text-left'>
            <div><strong>Source Type:</strong> {info.row.original.source_type}</div>
            <div><strong>Source ID:</strong> {info.row.original.source_id}</div>
          </div>
        </Tooltip>
        <CopyButtonWithAnimation content={info.getValue()}>
          <Copy size={16} strokeWidth={1} />
        </CopyButtonWithAnimation>
      </div>
    )
  }),
]

const DEFAULT_PAGE_SIZE = 50

interface LedgerEntryListProps {
  ledgerAccountId?: string
  ledgerAccountQuery?: string
  cutoff?: string
}

function LedgerEntryListImpl ({ ledgerAccountId, ledgerAccountQuery, cutoff }: LedgerEntryListProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  // Set all filters from the URL on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const pageParam = params.get('entries_page')
    const pageSizeParam = params.get('entries_page_size')
    const sortParam = params.get('entries_sort')

    if (pageParam) {
      setPagination(prev => ({
        ...prev,
        pageIndex: parseInt(pageParam, 10)
      }))
    }

    if (pageSizeParam) {
      setPagination(prev => ({
        ...prev,
        pageSize: parseInt(pageSizeParam, 10)
      }))
    }

    // Parse sort parameter (e.g., "created_at" or "-created_at")
    if (sortParam) {
      const isDescending = sortParam.startsWith('-')
      const column = isDescending ? sortParam.substring(1) : sortParam
      setSorting([{ id: column, desc: isDescending }])
    }
  }, [])

  // Update the URL on pagination and sorting change
  useEffect(() => {
    const pageURL = new URL(window.location.href)
    if (pagination.pageIndex) { pageURL.searchParams.set('entries_page', pagination.pageIndex.toString()) }

    // Update sort parameter
    if (sorting.length > 0) {
      const sortColumn = sorting[0].id
      const sortValue = sorting[0].desc ? `-${sortColumn}` : sortColumn
      pageURL.searchParams.set('entries_sort', sortValue)
    } else {
      pageURL.searchParams.delete('entries_sort')
    }

    // Use replaceState instead of pushState to avoid polluting browser history
    window.history.replaceState({}, '', pageURL)
  }, [pagination, sorting])

  const { data: ledgerEntriesData, isLoading, isError } = useGetLedgerEntries({
    ledgerAccountId,
    ledgerAccountQuery,
    cutoff,
    pagination,
    sorting,
  })

  const table = useReactTable({
    data: ledgerEntriesData?.data || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    state: {
      sorting,
      pagination,
    },
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='No ledger entries found.'
      setSorting={setSorting}
      defaultSortingState={[]}
      paginationLabelSingular='ledger entry'
      paginationLabelPlural='ledger entries'
      paginationMetadata={ledgerEntriesData?.pagination}
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: ledgerEntriesData?.pagination.next_page || 1,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: ledgerEntriesData?.pagination.prev_page || 1,
        })}
    />
  )
}

const LedgerEntryList = ({ ledgerAccountId, ledgerAccountQuery, cutoff }: LedgerEntryListProps) => {
  return (
    <LedgerEntryListImpl ledgerAccountId={ledgerAccountId} ledgerAccountQuery={ledgerAccountQuery} cutoff={cutoff} />
  )
}

export default LedgerEntryList
